import { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Snackbar,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { SelectMultipleWidget2 } from "../../../components";
import { alphabeticalSort } from "../../../helpers";
import { BsRow, BsCol } from "../../../layouts/components";
import { AxiosContext } from "../../../context/AxiosContext";

const PhEdit = ({
  open,
  onClose,
  selectedRow,
  onSuccess,
}) => {
  const axiosContext = useContext(AxiosContext);
  
  const [loadingEditSubmitState, setLoadingEditSubmitState] = useState(false);
  const [successEditSubmitState, setSuccessEditSubmitState] = useState(false);
  const [errorEditSubmitState, setErrorEditSubmitState] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [dataState, setDataState] = useState([]);
  const [productorIdOptions, setProductorIdOptions] = useState([]);
  const [agroBusinessOptions, setAgroBusinessOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [orchardOptions, setOrchardOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [varietyOptions, setVarietyOptions] = useState([]);
  const [productLineOptions, setProductLineOptions] = useState([]);
  const [technicBossOptions, setTechnicBossOptions] = useState([]);
  const [csgOptions, setCsgOptions] = useState([]);
  const [isNewCsg, setIsNewCsg] = useState(false);
  const [newCsgValue, setNewCsgValue] = useState("");
  const [isNewOrchard, setIsNewOrchard] = useState(false);
  const [newOrchardValue, setNewOrchardValue] = useState("");

  // Flags de visibilidad de la línea.
  // Planificación: null = la línea no tiene el campo ("Automático", según el Pactado).
  // Informe técnico: sin el campo la línea se ofrece, así que equivale a marcado.
  const initialShowPlanningHarvest = selectedRow?.showPlanningHarvest ?? null;
  const initialShowTechnicalReport = selectedRow?.showTechnicalReport !== false;
  const [showPlanningHarvestState, setShowPlanningHarvestState] = useState(null);
  const [showTechnicalReportState, setShowTechnicalReportState] = useState(true);

  const {
    handleSubmit: handleSubmitEdit,
    control: controlEdit,
    watch: watchEdit,
    setValue: setValueEdit,
    reset: resetEdit,
    formState: { errors: errorsEdit },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      productor: null,
      agroBusiness: null,
      group: null,
      orchard: null,
      specie: null,
      variety: null,
      productLine: null,
      technicBoss: null,
      csg: null,
    },
  });

  const watchEditProductor = watchEdit("productor");
  const watchEditAgroBusiness = watchEdit("agroBusiness");
  const watchEditGroup = watchEdit("group");
  const watchEditOrchard = watchEdit("orchard");
  const watchEditSpecie = watchEdit("specie");
  const watchEditVariety = watchEdit("variety");

  const endPointEdit = "v1/libraryExternal/update";
  const endPointCreate = "v1/libraryExternal/panel-admin-create";

  // Helper functions
  const getProductorObj = useCallback(() => {
    if (!watchEditProductor?.value || !dataState?.productorID) return null;
    return dataState.productorID.find(
      (item) => item.value === watchEditProductor.value
    );
  }, [watchEditProductor, dataState]);

  const getAgroBusinessObj = useCallback(() => {
    if (!watchEditAgroBusiness?.value || !dataState?.agroBusiness) return null;
    return dataState.agroBusiness.find(
      (item) => item.value === watchEditAgroBusiness.value
    );
  }, [watchEditAgroBusiness, dataState]);

  const getGroupObj = useCallback(() => {
    if (!watchEditGroup?.value || !dataState?.productorID) return null;
    
    // Buscar el grupo en la estructura jerárquica
    for (const productor of dataState.productorID) {
      if (productor.agroBusiness) {
        for (const agroBusiness of productor.agroBusiness) {
          if (agroBusiness.group) {
            const foundGroup = agroBusiness.group.find(
              (group) => group.value === watchEditGroup.value
            );
            if (foundGroup) {
              return foundGroup;
            }
          }
        }
      }
    }
    return null;
  }, [watchEditGroup, dataState]);

  const getOrchardObj = useCallback(() => {
    if (!watchEditOrchard?.value || !dataState?.orchard) return null;
    return dataState.orchard.find((item) => item.value === watchEditOrchard.value);
  }, [watchEditOrchard, dataState]);

  const getSpecieObj = useCallback(() => {
    if (!watchEditSpecie?.value || !dataState?.specie) return null;
    return dataState.specie.find((item) => item.value === watchEditSpecie.value);
  }, [watchEditSpecie, dataState]);

  const getVarietyObj = useCallback(() => {
    const specieObj = getSpecieObj();
    if (!specieObj || !watchEditVariety?.value) return null;
    return specieObj.variety?.find(
      (vari) => vari.value === watchEditVariety.value
    );
  }, [getSpecieObj, watchEditVariety]);

  // Función para enviar el formulario de edición
  const onSubmitEdit = async (values) => {

    setLoadingEditSubmitState(true);
    setErrorEditSubmitState(false);
    setErrorMessage("");

    try {
      // Construir el objeto Input con campos obligatorios y opcionales
      const dataToSend = {
        Input: {
          // Campos obligatorios (valores actuales de la fila)
          clientValue: selectedRow.clientValue || "",
          agroBusinessValue: selectedRow.agroBusinessValue || "",
          groupValue: selectedRow.groupValue || "",
          orchardValue: selectedRow.orchardValue || "",
          productLineValue: selectedRow.productLineValue || "",
          productorID: selectedRow.productorID || "",
          specieValue: selectedRow.specieValue || "",
          technicBossValue: selectedRow.technicBossValue || "",
          varietyValue: selectedRow.varietyValue || "",
          csg: selectedRow.csg || "",
          season: selectedRow.season || "2024",

          // Campos opcionales (nuevos valores si fueron seleccionados)
          ...(values.agroBusiness?.value && {
            newAgroBusiness: values.agroBusiness?.label,
            newAgroBusinessValue: values.agroBusiness?.value,
          }),
          ...(values.group?.value && {
            newGroup: values.group?.label,
            newGroupValue: values.group?.value,
          }),
          ...(values.orchard?.value && {
            newOrchard: values.orchard?.label,
            newOrchardValue: values.orchard?.value,
          }),
          ...((isNewOrchard && newOrchardValue.trim()) ? {
            newOrchard: newOrchardValue.trim(),
          } : {}),
          ...(values.variety?.value && {
            newVariety: values.variety?.label,
            newVarietyValue: values.variety?.value,
          }),
          ...(values.specie?.value && {
            newSpecie: values.specie?.label,
            newSpecieValue: values.specie?.value,
          }),
          ...(values.productLine?.value && {
            newProductLine: values.productLine?.label,
            newProductLineValue: values.productLine?.value,
          }),
          ...(values.technicBoss?.value && {
            newTechnicBoss: values.technicBoss?.label,
            newTechnicBossValue: values.technicBoss?.value,
          }),
          ...(values.productor?.value && {
            newProductorID: values.productor?.value,
          }),
          ...((isNewCsg && newCsgValue.trim()) ? {
            newCsg: newCsgValue.trim(),
          } : (values.csg?.value && {
            newCsg: values.csg?.value || values.csg?.label,
          })),

          // Flags de visibilidad: cada uno se envía solo si el usuario lo cambió
          ...(showPlanningHarvestState !== initialShowPlanningHarvest && {
            showPlanningHarvest: showPlanningHarvestState,
          }),
          ...(showTechnicalReportState !== initialShowTechnicalReport && {
            showTechnicalReport: showTechnicalReportState,
          }),
        },
      };

      console.log('📤 Enviando datos:', dataToSend);

      const response = await axiosContext.authAxios.post(
        endPointEdit,
        dataToSend
      );

      console.log('✅ Respuesta del servidor:', response);

      if (response.status === 200) {
        // El backend responde 200 también cuando no actualiza nada
        // (p. ej. "No se encontraron campos para actualizar").
        if (response.data?.success === false) {
          setErrorEditSubmitState(true);
          setErrorMessage(
            response.data?.message || "Error al actualizar el registro"
          );
          return;
        }
        setSuccessEditSubmitState(true);
        setErrorEditSubmitState(false);
        setShowSuccessSnackbar(true);
        if (onSuccess) {
          onSuccess();
        }
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error("❌ Error al actualizar:", error);
      setErrorEditSubmitState(true);
      
      // Manejar diferentes tipos de errores
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        const errorMsg = error.response.data?.message || 
                         error.response.data?.error ||
                         `Error del servidor: ${error.response.status}`;
        setErrorMessage(errorMsg);
        console.error("Error de respuesta:", error.response.data);
      } else if (error.request) {
        // La solicitud se hizo pero no se recibió respuesta
        setErrorMessage("No se recibió respuesta del servidor. Verifique su conexión.");
        console.error("Error de solicitud:", error.request);
      } else {
        // Algo sucedió al configurar la solicitud
        setErrorMessage(`Error: ${error.message}`);
        console.error("Error de configuración:", error.message);
      }
    } finally {
      setLoadingEditSubmitState(false);
    }
  };

  const handleClose = () => {
    resetEdit();
    setSuccessEditSubmitState(false);
    setErrorEditSubmitState(false);
    setErrorMessage("");
    setShowSuccessSnackbar(false);
    setIsNewCsg(false);
    setNewCsgValue("");
    setIsNewOrchard(false);
    setNewOrchardValue("");
    onClose();
  };

  const handleCloseSnackbar = () => {
    setShowSuccessSnackbar(false);
  };

  // Cargar datos del formulario
  const loadFormData = useCallback(async () => {
    try {
      const response = await axiosContext.authAxios.get(endPointCreate);
      const { data, status } = response;

      if (status === 200) {
        setDataState(data);
        setupOptionsFromData(data);
      }
    } catch (error) {
      console.error("Error loading form data:", error);
    }
  }, [axiosContext]);

  const setupOptionsFromData = useCallback((data) => {
    if (!data) return;

    const productorIds = data.productorID || [];
    setProductorIdOptions(alphabeticalSort(productorIds, "label"));

    const agroBusinesses = data.agroBusiness || [];
    setAgroBusinessOptions(alphabeticalSort(agroBusinesses, "label"));

    const orchards = data.orchard || [];
    setOrchardOptions(alphabeticalSort(orchards, "label"));

    const species = data.specie || [];
    setSpeciesOptions(alphabeticalSort(species, "label"));

    const technicBosses = data.technicBoss || [];
    setTechnicBossOptions(alphabeticalSort(technicBosses, "label"));
  }, []);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      loadFormData();
    }
  }, [open, loadFormData]);

  // Estado inicial de los checks de visibilidad, tomado de la fila
  useEffect(() => {
    if (open && selectedRow) {
      setShowPlanningHarvestState(selectedRow.showPlanningHarvest ?? null);
      setShowTechnicalReportState(selectedRow.showTechnicalReport !== false);
    }
  }, [open, selectedRow]);

  // No pre-cargar valores - el usuario seleccionará manualmente

  // useEffect para productor -> agroBusiness
  useEffect(() => {
    if (watchEditProductor?.value && dataState?.productorID) {
      const productorObj = getProductorObj();
      if (productorObj && productorObj.agroBusiness) {
        const agroBusinessData = productorObj.agroBusiness.map((agro) => ({
          label: agro.label,
          value: agro.value,
        }));
        setAgroBusinessOptions(alphabeticalSort(agroBusinessData, "label"));
      }
    }
  }, [watchEditProductor, dataState, getProductorObj]);

  // useEffect para agroBusiness -> group
  useEffect(() => {
    if (watchEditProductor?.value && watchEditAgroBusiness?.value && dataState?.productorID) {
      const productorObj = getProductorObj();
      if (productorObj && productorObj.agroBusiness) {
        const agroBusinessObj = productorObj.agroBusiness.find(
          (agro) => agro.value === watchEditAgroBusiness.value
        );
        if (agroBusinessObj && agroBusinessObj.group) {
          const groupData = agroBusinessObj.group.map((grp) => ({
            label: grp.label,
            value: grp.value,
          }));
          setGroupOptions(alphabeticalSort(groupData, "label"));
        }
      }
    }
  }, [watchEditProductor, watchEditAgroBusiness, dataState, getProductorObj]);

  // useEffect para group -> orchard
  useEffect(() => {
    if (watchEditProductor?.value && watchEditAgroBusiness?.value && watchEditGroup?.value && dataState?.productorID) {
      const groupObj = getGroupObj();
      if (groupObj && groupObj.orchard) {
        const orchardData = groupObj.orchard.map((orch) => ({
          label: orch.label,
          value: orch.value,
        }));
        setOrchardOptions(alphabeticalSort(orchardData, "label"));
      }
    }
  }, [watchEditProductor, watchEditAgroBusiness, watchEditGroup, dataState, getGroupObj]);

  // useEffect para orchard -> csg
  useEffect(() => {
    if (!dataState?.orchard) return;

    if (watchEditOrchard?.value) {
      // Si hay huerto seleccionado, filtrar CSG por ese huerto
      const orchardObj = getOrchardObj();
      if (orchardObj && orchardObj.csg) {
        const csgData = orchardObj.csg.map((csg) => ({
          label: csg.label,
          value: csg.value,
        }));
        setCsgOptions(alphabeticalSort(csgData, "label"));
      }
    } else {
      // Si no hay huerto seleccionado, mostrar todos los CSG disponibles
      const allCsgData = [];
      const csgSet = new Set(); // Para evitar duplicados
      
      dataState.orchard.forEach((orchard) => {
        if (orchard.csg) {
          orchard.csg.forEach((csg) => {
            const csgKey = `${csg.value}`;
            if (!csgSet.has(csgKey)) {
              csgSet.add(csgKey);
              allCsgData.push({
                label: csg.label,
                value: csg.value,
              });
            }
          });
        }
      });
      
      setCsgOptions(alphabeticalSort(allCsgData, "label"));
    }
  }, [watchEditOrchard, dataState, getOrchardObj]);

  // useEffect para specie -> variety
  useEffect(() => {
    if (watchEditSpecie?.value && dataState?.specie) {
      const specieObj = getSpecieObj();
      if (specieObj && specieObj.variety) {
        const varietyData = specieObj.variety.map((vari) => ({
          label: vari.label,
          value: vari.value,
        }));
        setVarietyOptions(alphabeticalSort(varietyData, "label"));
      }
    }
  }, [watchEditSpecie, dataState, getSpecieObj]);

  // useEffect para variety -> productLine
  useEffect(() => {
    if (watchEditVariety?.value && dataState?.specie) {
      const varietyObj = getVarietyObj();
      if (varietyObj && varietyObj.productLine) {
        const productLineData = varietyObj.productLine.map((prodLine) => ({
          label: prodLine.label,
          value: prodLine.value,
        }));
        setProductLineOptions(alphabeticalSort(productLineData, "label"));
      }
    }
  }, [watchEditVariety, dataState, getVarietyObj]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Editar Registro</DialogTitle>
      <DialogContent>
        {selectedRow && (
          <Box sx={{ pt: 2 }}>
            {successEditSubmitState && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Registro actualizado exitosamente
              </Alert>
            )}
            {errorEditSubmitState && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage || "Error al actualizar el registro"}
              </Alert>
            )}
            <form
              onSubmit={handleSubmitEdit(onSubmitEdit)}
              id="edit-form"
              style={{ padding: '0 8px' }}
            >
              <style>{`
                .no-gutters > [class*="col-"] {
                  padding-left: 8px !important;
                  padding-right: 8px !important;
                }
              `}</style>
              <BsRow class="no-gutters">
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Productor ID actual:</strong> {selectedRow.productorID || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.productor}
                    loading={false}
                    control={controlEdit}
                    name="productor"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={productorIdOptions}
                    placeholder="Seleccione Productor ID"
                  />
                </BsCol>
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Agrícola actual:</strong> {selectedRow.agroBusiness || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.agroBusiness}
                    loading={false}
                    control={controlEdit}
                    name="agroBusiness"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={agroBusinessOptions}
                    placeholder="Seleccione Agrícola"
                  />
                </BsCol>
              </BsRow>
              <BsRow class="no-gutters">
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Grupo actual:</strong> {selectedRow.group || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.group}
                    loading={false}
                    control={controlEdit}
                    name="group"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={groupOptions}
                    placeholder="Seleccione Grupo"
                  />
                </BsCol>
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Huerto actual:</strong> {selectedRow.orchard || 'N/A'}
                  </Box>
                  {isNewOrchard ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Ingrese el nuevo Huerto"
                      value={newOrchardValue}
                      onChange={(e) => setNewOrchardValue(e.target.value)}
                      size="small"
                    />
                  ) : (
                    <SelectMultipleWidget2
                      errors={errorsEdit.orchard}
                      loading={false}
                      control={controlEdit}
                      name="orchard"
                      display="vertical"
                      variant="outlined"
                      isClearable={true}
                      isSearchable
                      isMulti={false}
                      req={false}
                      defaultValue=""
                      options={orchardOptions}
                      placeholder="Seleccione Huerto"
                    />
                  )}
                </BsCol>
              </BsRow>
              <BsRow class="no-gutters">
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Especie actual:</strong> {selectedRow.specie || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.specie}
                    loading={false}
                    control={controlEdit}
                    name="specie"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={speciesOptions}
                    placeholder="Seleccione Especie"
                  />
                </BsCol>
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Variedad actual:</strong> {selectedRow.variety || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.variety}
                    loading={false}
                    control={controlEdit}
                    name="variety"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={varietyOptions}
                    placeholder="Seleccione Variedad"
                  />
                </BsCol>
              </BsRow>
              <BsRow class="no-gutters">
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Línea de Producto actual:</strong> {selectedRow.productLine || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.productLine}
                    loading={false}
                    control={controlEdit}
                    name="productLine"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={productLineOptions}
                    placeholder="Seleccione Línea de Producto"
                  />
                </BsCol>
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Jefe Técnico actual:</strong> {selectedRow.technicBoss || 'N/A'}
                  </Box>
                  <SelectMultipleWidget2
                    errors={errorsEdit.technicBoss}
                    loading={false}
                    control={controlEdit}
                    name="technicBoss"
                    display="vertical"
                    variant="outlined"
                    isClearable={true}
                    isSearchable
                    isMulti={false}
                    req={false}
                    defaultValue=""
                    options={technicBossOptions}
                    placeholder="Seleccione Jefe Técnico"
                  />
                </BsCol>
              </BsRow>
              <BsRow class="no-gutters">
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>CSG actual:</strong> {selectedRow.csg || 'N/A'}
                  </Box>
                  {isNewCsg ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Ingrese el nuevo CSG"
                      value={newCsgValue}
                      onChange={(e) => setNewCsgValue(e.target.value)}
                      size="small"
                    />
                  ) : (
                    <SelectMultipleWidget2
                      errors={errorsEdit.csg}
                      loading={false}
                      control={controlEdit}
                      name="csg"
                      display="vertical"
                      variant="outlined"
                      isClearable={true}
                      isSearchable
                      isMulti={false}
                      req={false}
                      defaultValue=""
                      options={csgOptions}
                      placeholder="Seleccione CSG"
                    />
                  )}
                </BsCol>
                <BsCol class="col-md-6">
                  <Box sx={{ mb: 1 }}>
                    <strong>Opciones:</strong>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isNewOrchard}
                          onChange={(e) => {
                            setIsNewOrchard(e.target.checked);
                            if (e.target.checked) {
                              setValueEdit("orchard", null);
                              setNewOrchardValue("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Ingresar Huerto nuevo"
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isNewCsg}
                          onChange={(e) => {
                            setIsNewCsg(e.target.checked);
                            if (e.target.checked) {
                              setValueEdit("csg", null);
                              setNewCsgValue("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Ingresar CSG nuevo"
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showPlanningHarvestState === true}
                          indeterminate={showPlanningHarvestState === null}
                          onChange={(e) =>
                            setShowPlanningHarvestState(e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label={
                        <span>
                          Ver en Planificación de cosecha
                          {showPlanningHarvestState === null && (
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                              }}
                            >
                              Automático: según el Pactado cargado
                            </Box>
                          )}
                        </span>
                      }
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showTechnicalReportState}
                          onChange={(e) =>
                            setShowTechnicalReportState(e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label="Ver en Informe técnico"
                      sx={{ m: 0 }}
                    />
                  </Box>
                </BsCol>
              </BsRow>
            </form>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loadingEditSubmitState}
        >
          Cerrar
        </Button>
        <Button
          color="primary"
          size="medium"
          variant="contained"
          disabled={loadingEditSubmitState}
          onClick={handleSubmitEdit(onSubmitEdit)}
        >
          {loadingEditSubmitState ? "Guardando..." : successEditSubmitState ? "Guardado ✓" : "Guardar"}
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={showSuccessSnackbar}
        onClose={handleCloseSnackbar}
        autoHideDuration={3000}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Guardado correctamente
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PhEdit;
